/**
 * Word API Helper Utility
 * 
 * Provides centralized error handling for Office.js Word API operations.
 * Addresses Issue #9: Word API Error Handling Missing
 * 
 * Features:
 * - Safe context.sync() wrapper with error handling
 * - Word API availability checks
 * - User-friendly error messages for Word API failures
 * - Consistent error handling across all features
 */

import { handleError } from './errorHandler';

/**
 * Error types specific to Word API operations
 */
export enum WordApiErrorType {
  SYNC_FAILED = 'sync_failed',
  API_UNAVAILABLE = 'api_unavailable',
  DOCUMENT_ACCESS = 'document_access',
  SELECTION_FAILED = 'selection_failed',
  INSERTION_FAILED = 'insertion_failed',
  SEARCH_FAILED = 'search_failed',
}

/**
 * Safely execute context.sync() with proper error handling
 * 
 * @param context - Word.RequestContext
 * @param operation - Description of the operation being performed (for error messages)
 * @returns Promise<void>
 * @throws Error with user-friendly message if sync fails
 */
export const safeSync = async (
  context: Word.RequestContext,
  operation: string = 'access Word document'
): Promise<void> => {
  try {
    await context.sync();
  } catch (syncError: unknown) {
    const err = syncError as { code?: string; message?: string };
    // Check for specific Word API error codes
    if (err.code === 'AccessDenied') {
      throw new Error('Access denied. Please ensure you have permission to access this document.');
    } else if (err.code === 'GeneralException') {
      throw new Error(`Failed to ${operation}. The document may be corrupted or inaccessible.`);
    } else if (err.message?.includes('not available')) {
      throw new Error('Word API is not available. Please ensure you are running this add-in in Microsoft Word.');
    } else if (err.message?.includes('network')) {
      throw new Error(`Network error while trying to ${operation}. Please check your connection.`);
    } else {
      // Generic sync error
      throw new Error(`Failed to ${operation}. Please ensure the document is open and accessible.`);
    }
  }
};

/**
 * Check if Word API is available
 * 
 * @returns boolean - true if Word API is available
 */
export const isWordApiAvailable = (): boolean => {
  return typeof Word !== 'undefined' && typeof Word.run === 'function';
};

/**
 * Safely execute a Word.run operation with comprehensive error handling
 * 
 * @param operation - The Word.run operation to execute
 * @param featureName - Name of the feature calling this function (for error tracking)
 * @param metadata - Additional metadata for error logging
 * @returns Promise<T> - Result of the operation
 * @throws AppError with user-friendly message if operation fails
 */
export const safeWordRun = async <T>(
  operation: (context: Word.RequestContext) => Promise<T>,
  featureName: string,
  metadata?: Record<string, unknown>
): Promise<T> => {
  // Check if Word API is available
  if (!isWordApiAvailable()) {
    const error = new Error('Word API is not available. Please ensure you are running this add-in in Microsoft Word.');
    throw handleError(error, featureName, { 
      ...metadata, 
      errorType: WordApiErrorType.API_UNAVAILABLE 
    });
  }

  try {
    return await Word.run(operation);
  } catch (err: unknown) {
    // Classify the error
    let errorType = WordApiErrorType.DOCUMENT_ACCESS;
    const errMsg = err instanceof Error ? err.message : '';

    if (errMsg.includes('sync')) {
      errorType = WordApiErrorType.SYNC_FAILED;
    } else if (errMsg.includes('selection')) {
      errorType = WordApiErrorType.SELECTION_FAILED;
    } else if (errMsg.includes('insert')) {
      errorType = WordApiErrorType.INSERTION_FAILED;
    } else if (errMsg.includes('search')) {
      errorType = WordApiErrorType.SEARCH_FAILED;
    }

    throw handleError(err, featureName, {
      ...metadata,
      errorType,
      operation: 'Word.run'
    });
  }
};

/**
 * Get selected text from Word document with error handling
 * 
 * @param featureName - Name of the feature calling this function
 * @returns Promise<string> - Selected text
 * @throws AppError if selection fails
 */
export const getSelectedText = async (featureName: string): Promise<string> => {
  return await safeWordRun(async (context) => {
    const selection = context.document.getSelection();
    context.load(selection, 'text');
    await safeSync(context, 'get selected text');
    return selection.text;
  }, featureName, { operation: 'getSelectedText' });
};

/**
 * Get document body text with error handling
 * 
 * @param featureName - Name of the feature calling this function
 * @returns Promise<string> - Document body text
 * @throws AppError if operation fails
 */
export const getDocumentText = async (featureName: string): Promise<string> => {
  return await safeWordRun(async (context) => {
    const body = context.document.body;
    context.load(body, 'text');
    await safeSync(context, 'get document text');
    return body.text;
  }, featureName, { operation: 'getDocumentText' });
};

/**
 * Get selected text or document text (fallback) with error handling
 * 
 * @param featureName - Name of the feature calling this function
 * @returns Promise<{ text: string, isSelection: boolean }> - Text and whether it's from selection
 * @throws AppError if operation fails
 */
export const getTextOrSelection = async (
  featureName: string
): Promise<{ text: string; isSelection: boolean }> => {
  return await safeWordRun(async (context) => {
    const selection = context.document.getSelection();
    context.load(selection, 'text');
    await safeSync(context, 'get selected text');

    if (selection.text && selection.text.trim().length > 0) {
      return { text: selection.text, isSelection: true };
    } else {
      const body = context.document.body;
      context.load(body, 'text');
      await safeSync(context, 'get document text');
      return { text: body.text, isSelection: false };
    }
  }, featureName, { operation: 'getTextOrSelection' });
};

/**
 * Insert text at selection with error handling
 * 
 * @param text - Text to insert
 * @param location - Insert location (replace, before, after, start, end)
 * @param featureName - Name of the feature calling this function
 * @returns Promise<void>
 * @throws AppError if insertion fails
 */
export const insertTextAtSelection = async (
  text: string,
  location: Word.InsertLocation,
  featureName: string
): Promise<void> => {
  await safeWordRun(async (context) => {
    const selection = context.document.getSelection();
    selection.insertText(text, location);
    await safeSync(context, 'insert text');
  }, featureName, { operation: 'insertTextAtSelection', textLength: text.length });
};

/**
 * Search and replace text in document with error handling
 * 
 * @param searchText - Text to search for
 * @param replaceText - Text to replace with
 * @param featureName - Name of the feature calling this function
 * @returns Promise<number> - Number of replacements made
 * @throws AppError if operation fails
 */
export const searchAndReplace = async (
  searchText: string,
  replaceText: string,
  featureName: string
): Promise<number> => {
  return await safeWordRun(async (context) => {
    const range = context.document.body.search(searchText);
    context.load(range);
    await safeSync(context, 'search for text');

    if (range.items.length > 0) {
      range.items[0].insertText(replaceText, Word.InsertLocation.replace);
      await safeSync(context, 'replace text');
      return 1;
    }
    return 0;
  }, featureName, { 
    operation: 'searchAndReplace', 
    searchTextLength: searchText.length,
    replaceTextLength: replaceText.length
  });
};

/**
 * Search for all occurrences of text in document with error handling
 * 
 * @param searchText - Text to search for
 * @param featureName - Name of the feature calling this function
 * @returns Promise<number> - Number of matches found
 * @throws AppError if operation fails
 */
export const searchText = async (
  searchText: string,
  featureName: string
): Promise<number> => {
  return await safeWordRun(async (context) => {
    const range = context.document.body.search(searchText);
    context.load(range);
    await safeSync(context, 'search for text');
    return range.items.length;
  }, featureName, { 
    operation: 'searchText', 
    searchTextLength: searchText.length
  });
};

/**
 * Insert table at selection with error handling
 * 
 * @param rowCount - Number of rows
 * @param columnCount - Number of columns
 * @param data - Table data (2D array)
 * @param featureName - Name of the feature calling this function
 * @returns Promise<void>
 * @throws AppError if insertion fails
 */
export const insertTable = async (
  rowCount: number,
  columnCount: number,
  data: string[][],
  featureName: string
): Promise<void> => {
  await safeWordRun(async (context) => {
    const selection = context.document.getSelection();
    const table = selection.insertTable(rowCount, columnCount, Word.InsertLocation.after, data);
    table.headerRowCount = 1;
    await safeSync(context, 'insert table');
  }, featureName, { 
    operation: 'insertTable', 
    rowCount, 
    columnCount 
  });
};

